import { createFileRoute, Link } from "@tanstack/react-router";
import { Aperture, ArrowRight, BookOpen, Camera, Layers, Smartphone, Wand2 } from "lucide-react";
import heroCamera from "@/assets/hero-camera.jpg";
import { blogPosts } from "@/content/blog";

const URL = "https://fotopasion.es/blog";
const DESCRIPTION =
  "Consejos, guías y ejercicios prácticos para aprender fotografía, mejorar tu técnica y desarrollar tu mirada.";

const topics = [
  {
    icon: Aperture,
    title: "Técnica fotográfica",
    description: "Exposición, enfoque y ajustes explicados de forma clara.",
  },
  {
    icon: Layers,
    title: "Composición",
    description: "Recursos para ordenar el encuadre y dirigir la mirada.",
  },
  {
    icon: Smartphone,
    title: "Fotografía móvil",
    description: "Ideas prácticas para aprovechar la cámara que llevas contigo.",
  },
  {
    icon: Wand2,
    title: "Edición",
    description: "Procesos sencillos para revelar y mejorar tus fotografías con criterio.",
  },
];

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog de fotografía · FotoPasión" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Blog de fotografía · FotoPasión" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: Blog,
});

function Blog() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur-lg">
        <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="FotoPasión" width={96} height={96} className="h-12 w-auto" />
            <span className="hidden text-lg font-bold sm:inline">FotoPasión</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/guia-principiantes"
              className="hidden text-sm font-medium text-muted-foreground transition hover:text-foreground sm:inline"
            >
              Guía para principiantes
            </Link>
            <Link
              to="/"
              hash="cursos"
              className="inline-flex items-center gap-2 rounded-full bg-cta px-5 py-2.5 text-sm font-semibold text-cta-foreground shadow-[var(--shadow-soft)] transition hover:brightness-95"
            >
              Ver cursos <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </nav>
      </header>

      <main>
        <section
          className="relative overflow-hidden"
          style={{ background: "var(--gradient-hero)" }}
        >
          <div className="absolute inset-0" style={{ background: "var(--gradient-hero-glow)" }} />
          <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-6 py-14 md:px-8 md:py-20 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <nav aria-label="Ruta de navegación" className="text-sm text-muted-foreground">
                <Link to="/" className="font-medium text-primary hover:underline">
                  Inicio
                </Link>
                <span className="mx-2" aria-hidden="true">
                  /
                </span>
                <span aria-current="page">Blog</span>
              </nav>
              <span className="mt-7 inline-flex items-center gap-2 rounded-full bg-white/75 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary backdrop-blur">
                <BookOpen className="h-4 w-4" /> Aprende y practica
              </span>
              <h1 className="mt-4 max-w-3xl text-balance font-display text-4xl font-bold leading-[1.08] tracking-tight md:text-5xl lg:text-6xl">
                El blog de <span className="italic text-primary">FotoPasión</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                Guías, consejos y ejercicios para entender mejor tu cámara, entrenar la mirada y
                disfrutar del aprendizaje fotográfico.
              </p>
            </div>
            <div className="relative">
              <div
                className="absolute -inset-5 rounded-[2.5rem] opacity-40 blur-3xl"
                style={{ background: "var(--gradient-primary)" }}
              />
              <img
                src={heroCamera}
                alt="Cámara fotográfica preparada para aprender"
                width={1920}
                height={1080}
                className="relative aspect-[4/3] w-full rounded-[2rem] border-8 border-white/70 object-cover shadow-[var(--shadow-soft)]"
              />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-16 md:px-8 md:py-24">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Artículos
            </span>
            <h2 className="mt-2 text-balance font-display text-4xl font-bold md:text-5xl">
              Últimas publicaciones
            </h2>
          </div>

          {blogPosts.length > 0 ? (
            <div className="mt-10 grid gap-6 md:mt-14 md:grid-cols-2 lg:grid-cols-3">
              {blogPosts.map((post) => (
                <article
                  key={post.slug}
                  className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:border-primary/35 hover:shadow-[var(--shadow-soft)]"
                >
                  <img
                    src={post.featuredImage}
                    alt={post.featuredImageAlt}
                    width={800}
                    height={520}
                    className="aspect-[16/10] w-full object-cover"
                  />
                  <div className="flex flex-1 flex-col p-7">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {post.category}
                    </span>
                    <h3 className="mt-3 font-display text-2xl font-bold leading-tight">
                      {post.title}
                    </h3>
                    <p className="mt-3 flex-1 leading-7 text-muted-foreground">
                      {post.description}
                    </p>
                    <Link
                      to="/blog/$slug"
                      params={{ slug: post.slug }}
                      className="mt-7 inline-flex items-center gap-2 font-semibold text-primary"
                    >
                      Leer artículo{" "}
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-10 grid items-center gap-8 rounded-[2rem] border border-primary/15 bg-card p-8 shadow-sm md:mt-14 md:grid-cols-[auto_1fr_auto] md:p-10">
              <span className="grid h-16 w-16 place-items-center rounded-2xl bg-secondary text-cta">
                <Camera className="h-8 w-8" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-primary">
                  En preparación
                </p>
                <h3 className="mt-2 font-display text-2xl font-bold md:text-3xl">
                  Los primeros artículos llegarán pronto
                </h3>
                <p className="mt-3 max-w-2xl leading-7 text-muted-foreground">
                  Mientras preparamos el blog, puedes empezar por nuestra guía completa para
                  aprender fotografía desde cero.
                </p>
              </div>
              <Link
                to="/guia-principiantes"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-cta px-6 py-3 font-semibold text-cta-foreground transition hover:brightness-95"
              >
                Leer la guía <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </section>

        <section className="bg-secondary/10">
          <div className="mx-auto max-w-7xl px-6 py-16 md:px-8 md:py-24">
            <div className="mx-auto max-w-2xl text-center">
              <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                Temas del blog
              </span>
              <h2 className="mt-2 text-balance font-display text-4xl font-bold md:text-5xl">
                Una biblioteca para avanzar paso a paso
              </h2>
            </div>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {topics.map((topic) => {
                const Icon = topic.icon;
                return (
                  <article
                    key={topic.title}
                    className="rounded-3xl border border-border bg-card p-7"
                  >
                    <span className="grid h-12 w-12 place-items-center rounded-2xl bg-secondary text-cta">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-5 text-lg font-semibold tracking-tight">{topic.title}</h3>
                    <p className="mt-2 leading-7 text-muted-foreground">{topic.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 py-16 md:px-8 md:py-24">
          <div className="overflow-hidden rounded-[2rem] bg-secondary p-8 text-center text-secondary-foreground shadow-[var(--shadow-soft)] md:p-12">
            <BookOpen className="mx-auto h-8 w-8 text-cta" />
            <span className="mt-4 block text-sm font-semibold uppercase tracking-wider text-cta">
              Punto de partida
            </span>
            <h2 className="mx-auto mt-2 max-w-3xl text-balance font-display text-3xl font-bold md:text-4xl">
              ¿Estás empezando en fotografía?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-7 text-secondary-foreground/75">
              Sigue una ruta clara para conocer tu cámara, comprender la luz, componer y empezar a
              editar.
            </p>
            <Link
              to="/guia-principiantes"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-cta px-7 py-3.5 font-semibold text-cta-foreground transition hover:brightness-95"
            >
              Ir a la guía para principiantes <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>

      <footer className="bg-foreground text-background">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-6 py-12 md:flex-row md:items-center md:justify-between md:px-8">
          <div>
            <div className="flex items-center gap-2">
              <img
                src="/logo.png"
                alt="FotoPasión"
                width={96}
                height={96}
                className="h-10 w-auto"
              />
              <span className="text-lg font-bold">FotoPasión</span>
            </div>
            <p className="mt-3 text-sm text-background/70">
              Formación online para aprender fotografía con criterio y práctica.
            </p>
          </div>
          <span className="text-sm text-background/70">
            © {new Date().getFullYear()} FotoPasión
          </span>
        </div>
      </footer>
    </div>
  );
}
